type ExceptionEvent = { properties?: Record<string, unknown> };

// Third-party noise that auto-captured exceptions can't act on: cross-origin
// scripts stripped to a bare "Script error.", the Google Maps loader failing
// to fetch its sub-modules, Maps being blocked by privacy extensions, and
// Microsoft's SafeLinks scanner whose injected instrumentation rejects.
const NOISE_PATTERNS = [
  /^Script error\.?$/,
  // Chunk fetch failures are connectivity (mobile timeouts) or stale-deploy
  // races, and each one drags the React #423 hydration error along with it —
  // neither is a code defect worth quota.
  /^Loading chunk .+ failed/,
  /^Minified React error #423/,
  /^ResizeObserver loop/,
  /^Could not load "[^"]+"\.?$/,
  /^useLoadScript was marked as loaded/,
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id/,
];

// Match against `$exception_list`, the only shape posthog-js sends: the flat
// `$exception_values` / `$exception_types` arrays are derived during ingestion
// and are absent when `before_send` runs.
export const isNoiseException = (event: ExceptionEvent) => {
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.every((e: any) => NOISE_PATTERNS.some((p) => p.test(String(e?.value ?? ''))));
};

// An exception whose stack frames all live outside our origin was thrown by a
// third-party script (Google Maps, host-page embeds) — not our defect.
export const isThirdPartyException = (event: ExceptionEvent) => {
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list)) return false;
  const frames = list.flatMap((e: any) => e?.stacktrace?.frames ?? []);
  const urlFrames = frames.filter((f: any) => typeof f?.filename === 'string' && /^https?:/.test(f.filename));
  if (urlFrames.length === 0) return false;
  return urlFrames.every((f: any) => !f.filename.startsWith(window.location.origin));
};
